import React, { useState, useContext, useRef, useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Card, CardBody } from "@windmill/react-ui";
import { useForm } from "react-hook-form";
import Title from "components/form/Title";
import LabelArea from "components/form/LabelArea";
import Error from "components/form/Error";
import DrawerButton from "components/form/DrawerButton";
import { SidebarContext } from "context/SidebarContext";
import { notifyError, notifySuccess } from "utils/toast";
import WhatsAppServices from "services/WhatsAppServices";
import FileUploader from "components/templatefileUploader/FileUploader";
import CharacterLimitedInput from "components/characterLimits/CharacterLimitedInput";

const getNextVarNumber = (text) => {
  const regex = /\{\{(\d+)\}\}/g;
  let max = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const num = parseInt(match[1], 10);
    if (num > max) max = num;
  }

  return max + 1;
};

const TemplateDrawer = ({ id }) => {
  const { setIsUpdate, toggleDrawer , isDrawerOpen} = useContext(SidebarContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [selectFileType, setselectFileType] = useState("none");
  const [headerCounter, setHeaderCounter] = useState(1);
  const [bodyCounter, setBodyCounter] = useState(1);
  const [headerVariables, setHeaderVariables] = useState([]);
  const [bodyVariables, setBodyVariables] = useState([]);

  // refs for header/body textareas (for inserting variable placeholders)
  const headerRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!isDrawerOpen) {
      reset({
        name: "",
        category: "",
        language: "",
        variableType: "",
        media: "none",
        header: "",
        body: "",
        footer: "",
        buttons: [],
        fileUrl: "",
        fileLocation: null,
      });
      setselectFileType("none");
      setHeaderVariables([]);
      setBodyVariables([]);
      setHeaderCounter(1);
      setBodyCounter(1);
    }
  }, [isDrawerOpen, reset]);

  const insertVariable = (ref, fieldName) => {
    if (ref.current) {
      const textarea = ref.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = getValues(fieldName) || "";

      const type = getValues("variableType"); // "name" or "number"
      let variableToInsert = "";

      if (type === "number") {
        // compute next available slot dynamically
        const nextNumber = getNextVarNumber(currentValue);
        variableToInsert = `{{${nextNumber}}}`;
      } else if (type === "name") {
        variableToInsert = "{{name}}";
      }

      // Insert into text
      const newValue =
        currentValue.substring(0, start) +
        variableToInsert +
        currentValue.substring(end);

      setValue(fieldName, newValue);

      // Sync AFTER insertion
      if (fieldName === "header") {
        syncVariables("header", headerVariables, setHeaderVariables);
      } else if (fieldName === "body") {
        syncVariables("body", bodyVariables, setBodyVariables);
      }

      // Restore cursor
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd =
          start + variableToInsert.length;
        textarea.focus();
      }, 0);
    }
  };

  const syncVariables = (fieldName, variables, setVariables) => {
    let text = getValues(fieldName) || "";
    const regex = /\{\{\d+\}\}/g;

    // Find all placeholders in text (in order they appear)
    const matches = [...text.matchAll(regex)].map((m) => m[0]);

    // Reindex them sequentially {{1}}, {{2}}, ...
    let counter = 1;
    const updatedText = text.replace(regex, () => `{{${counter++}}}`);

    // Update textarea value if text changed
    if (updatedText !== text) {
      setValue(fieldName, updatedText);
      text = updatedText;
    }

    // Build new variable array with preserved values if possible
    counter = 1;
    const newVars = (text.match(regex) || []).map((m) => {
      const newKey = `{{${counter++}}}`;
      const existing = variables.find((v) => v.key === m);
      return { key: newKey, value: existing ? existing.value : "" };
    });

    setVariables(newVars);
  };

const onSubmit = async (formData) => {
  setLoading(true);
  try {
    const components = [];

    // === HEADER Component (optional) ===
    if (
      formData.header ||
      (["image", "video", "document"].includes(selectFileType) && formData.fileUrl)
    ) {
      const headerComp = {
        type: "HEADER",
      };
      //  console.log("header structure---",headerComp);
       
      switch (selectFileType) {
        // TEXT HEADER
        case "none":
          headerComp.format = "TEXT";
          headerComp.text = formData.header;
          break;

        // IMAGE / VIDEO / DOCUMENT HEADER
        case "image":
        case "video":
        case "document":
          headerComp.format = selectFileType.toUpperCase();
          if (!formData.fileUrl) {
            notifyError(`Please upload a ${selectFileType} file`);
            setLoading(false);
            return;
          }
          // WhatsApp expects example inside "example.header_handle"
          headerComp.example = {
            header_handle: [formData.fileUrl],
          };
          break;

        // LOCATION HEADER
        /*case "location":
          headerComp.format = "LOCATION";
          if (
            !formData.fileLocation ||
            !formData.fileLocation.latitude ||
            !formData.fileLocation.longitude
          ) {
            notifyError("Please provide valid location coordinates");
            setLoading(false);
            return;
          }
          headerComp.example = {
            header_handle: [
              {
                latitude: formData.fileLocation.latitude,
                longitude: formData.fileLocation.longitude,
                name: formData.fileLocation.name || "",
                address: formData.fileLocation.address || "",
              },
            ],
          };
          break; */

        // DEFAULT
        default:
          headerComp.format = "TEXT";
          headerComp.text = formData.header;
      }

      components.push(headerComp);
    }

    // === BODY Component (required) ===
    if (!formData.body || formData.body.trim() === "") {
      notifyError("Body is required");
      setLoading(false);
      return;
    }
    
    const bodyExampleValues = bodyVariables.map(v => v.value || "sample"); 
    components.push({
      type: "BODY",
      text: formData.body,
      example: {
        body_text: [bodyExampleValues]
      }
    });

    // === FOOTER Component (optional) ===
    if (formData.footer) {
      components.push({
        type: "FOOTER",
        text: formData.footer,
      });
    }

    // === BUTTONS Component (optional) ===
    if (formData.buttons && formData.buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons: formData.buttons.map((b) => {
          if (b.type === "URL")
            return { type: "URL", text: b.text, url: b.url };
          if (b.type === "PHONE_NUMBER")
            return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
          return { type: "QUICK_REPLY", text: b.text };
        }),
      });
    }

    const payload = {
      name: formData.name,
      language: formData.language,
      category: formData.category,
      components,
    };

    console.log("WhatsApp Template Payload:", payload);

    await WhatsAppServices.addTemplate(payload);
    notifySuccess("Template created successfully");
    setIsUpdate(true);
    toggleDrawer();
  } catch (err) {
    console.error("Template submit failed:", err);
    notifyError(err.response?.data?.message || "Failed to create template");
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      {/* Drawer Header */}
      <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <Title register={register} title="Add Template" />
      </div>

      {/* Drawer Body */}
      <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
        <Card className="flex-grow scrollbar-hide w-full max-h-full">
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="px-2 flex-grow w-full max-h-full pb-20 grid grid-cols-12 gap-6 items-center">
                {/* Template Name */}
                <div className="col-span-6">
                  <LabelArea required label="Template Name" />
                  <div className="relative w-full">
                    <input
                      {...register("name", {
                        required: "Template name is required",
                        pattern: {
                          value: /^[a-z0-9_]+$/,
                          message:
                            "Only lowercase letters, numbers, and underscores allowed",
                        },
                      })}
                      type="text"
                      placeholder="e.g. welcome_offer"
                      maxLength={512}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200 pr-12"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(
                          /[^a-z0-9_]/g,
                          ""
                        );
                      }}
                    />
                    {/* Counter inside the box, right side, vertically centered */}
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                      {watch("name")?.length || 0}/512
                    </span>
                  </div>
                  <Error errorName={errors.name} />
                </div>

                {/* Category */}
                <div className="col-span-6">
                  <LabelArea required label="Category" />
                  <select
                    {...register("category", {
                      required: "Category is required",
                    })}
                    defaultValue=""
                    className="w-full px-3 py-2 border border-gray-300 rounded-md 
               dark:bg-gray-700 dark:text-gray-200"
                  >
                    <option value="" disabled>
                      --Choose Category--
                    </option>
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utility</option>
                    <option value="AUTHENTICATION">Authentication</option>
                  </select>
                  <Error errorName={errors.category} />
                </div>

                {/* Language */}
                <div className="col-span-6">
                  <LabelArea required label="Language" />
                  <select
                    {...register("language", {
                      required: "Language is required",
                    })}
                    defaultValue=""
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200"
                  >
                    <option value="" disabled>
                      --Choose Language--
                    </option>
                    <option value="en_US">English</option>
                    <option value="hi_IN">Hindi</option>
                    {/* <option value="es">Spanish</option> */}
                  </select>
                  <Error errorName={errors.language} />
                </div>

                {/* Variable Type */}
                <div className="col-span-6">
                  <LabelArea label="Type of Variable" />
                  <select
                    {...register("variableType", {
                      required: "Variable type is required",
                    })}
                    defaultValue=""
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200"
                  >
                    <option value="" disabled>
                      --Choose Variable Type--
                    </option>
                    <option value="name">Name</option>
                    <option value="number">Number</option>
                  </select>
                  <Error errorName={errors.variableType} />
                </div>

                {/* Media Type */}
                <div className="col-span-6">
                  <LabelArea label="Media sample" />
                  <select
                    {...register("media")}
                    defaultValue="none"
                    onChange={(e) => {
                      setValue("media", e.target.value);
                      setselectFileType(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200"
                  >
                    <option value="none">Text</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                    {/* <option value="location">Location</option> */}
                  </select>
                </div>

                {/* File Uploader */}
                {selectFileType !== "none" && (
                  <div className="col-span-12 mt-2">
                    <FileUploader
                      fileType={selectFileType}
                      setfileData={(url) => setValue("fileUrl", url)}
                      fileData={getValues("fileUrl")}
                      folder="whatsapp"
                      onUploadComplete={(url) => setValue("fileUrl", url)}
                    />
                  </div>
                )}

                {/* Header */}
                <div className="col-span-12">
                  <LabelArea label="Header" />
                  <div className="relative w-full">
                    <textarea
                      {...register("header", {
                        maxLength: {
                          value: 60,
                          message: "Header cannot exceed 60 characters",
                        },
                      })}
                      ref={(e) => {
                        register("header").ref(e); // attach RHF ref
                        headerRef.current = e; // attach local ref
                      }}
                      rows={2}
                      placeholder="Enter Text"
                      className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200 resize-none"
                      onChange={(e) => {
                        setValue("header", e.target.value);
                        syncVariables(
                          "header",
                          headerVariables,
                          setHeaderVariables
                        );
                      }}
                    />
                    <span className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
                      {watch("header")?.length || 0}/60
                    </span>
                  </div>
                  <Error errorName={errors.header} />
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => insertVariable(headerRef, "header")}
                      className="text-blue-500 hover:underline text-sm"
                    >
                      + Add Variable
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="col-span-12">
                  <LabelArea required label="Body" />
                  <div className="relative w-full">
                    <textarea
                      {...register("body", {
                        required: "Body is required",
                        maxLength: {
                          value: 1024,
                          message: "Body cannot exceed 1024 characters",
                        },
                      })}
                      ref={(e) => {
                        register("body").ref(e); // attach RHF ref
                        bodyRef.current = e; // attach local ref
                      }}
                      rows={6}
                      placeholder="Enter Text"
                      className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200 resize-none"
                      onChange={(e) => {
                        setValue("body", e.target.value);
                        syncVariables("body", bodyVariables, setBodyVariables);
                      }}
                    />
                    <span className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
                      {watch("body")?.length || 0}/1024
                    </span>
                  </div>
                  <Error errorName={errors.body} />
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => insertVariable(bodyRef, "body")}
                      className="text-blue-500 hover:underline text-sm"
                    >
                      + Add Variable
                    </button>
                  </div>
                </div>

                {/* Variable Samples */}
                {(headerVariables.length > 0 || bodyVariables.length > 0) && (
                  <div className="col-span-12 mt-4">
                    <LabelArea label="Variable Samples" />
                    <div className="space-y-4">
                      {/* Header Variables */}
                      {headerVariables.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold mb-2">
                            Header Variables
                          </p>
                          {headerVariables.map((v, idx) => (
                            <div
                              key={`header-var-${idx}`}
                              className="flex items-center space-x-2 mb-2"
                            >
                              <span className="px-2 py-1 bg-gray-100 border rounded text-sm">
                                {v.key}
                              </span>
                              <input
                                type="text"
                                value={v.value}
                                onChange={(e) =>
                                  setHeaderVariables((prev) => {
                                    const newArr = [...prev];
                                    newArr[idx].value = e.target.value;
                                    return newArr;
                                  })
                                }
                                placeholder={`Value for ${v.key}`}
                                className="flex-1 px-2 py-1 border rounded text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Body Variables */}
                      {bodyVariables.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold mb-2">
                            Body Variables
                          </p>
                          {bodyVariables.map((v, idx) => (
                            <div
                              key={`body-var-${idx}`}
                              className="flex items-center space-x-2 mb-2"
                            >
                              <span className="px-2 py-1 bg-gray-100 border rounded text-sm">
                                {v.key}
                              </span>
                              <input
                                type="text"
                                value={v.value}
                                onChange={(e) =>
                                  setBodyVariables((prev) => {
                                    const newArr = [...prev];
                                    newArr[idx].value = e.target.value;
                                    return newArr;
                                  })
                                }
                                placeholder={`Value for ${v.key}`}
                                className="flex-1 px-2 py-1 border rounded text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="col-span-12">
                  <LabelArea label="Footer" />
                  <div className="relative w-full">
                    <textarea
                      {...register("footer", {
                        maxLength: {
                          value: 60,
                          message: "Footer cannot exceed 60 characters",
                        },
                      })}
                      rows={2}
                      placeholder="Enter Text"
                      className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-gray-200 resize-none"
                    />
                    <span className="absolute bottom-2 right-2 text-xs text-gray-400 pointer-events-none">
                      {watch("footer")?.length || 0}/60
                    </span>
                  </div>
                  <Error errorName={errors.footer} />
                </div>

                {/* Buttons */}
                <div className="col-span-12 mt-4">
                  <LabelArea label="Buttons" />
                  <div className="space-y-3">
                    {watch("buttons")?.map((btn, idx) => (
                      <div
                        key={`button-${idx}`}
                        className="flex items-center space-x-2 border p-2 rounded-md"
                      >
                        {/* Button Type */}
                        <select
                          value={btn.type}
                          onChange={(e) => {
                            const updated = [...getValues("buttons")];
                            updated[idx].type = e.target.value;
                            setValue("buttons", updated);
                          }}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          <option value="QUICK_REPLY">Quick Reply</option>
                          <option value="URL">URL</option>
                          <option value="PHONE_NUMBER">Phone</option>
                        </select>

                        {/* Button Text */}
                        <input
                          type="text"
                          value={btn.text}
                          onChange={(e) => {
                            const updated = [...getValues("buttons")];
                            updated[idx].text = e.target.value;
                            setValue("buttons", updated);
                          }}
                          placeholder="Button Text"
                          className="flex-1 px-2 py-1 border rounded text-sm"
                        />

                        {/* Extra input for URL */}
                        {btn.type === "URL" && (
                          <input
                            type="text"
                            value={btn.url || ""}
                            onChange={(e) => {
                              const updated = [...getValues("buttons")];
                              updated[idx].url = e.target.value;
                              setValue("buttons", updated);
                            }}
                            placeholder="https://example.com"
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                        )}

                        {/* Extra input for Phone */}
                        {btn.type === "PHONE_NUMBER" && (
                          <input
                            type="text"
                            value={btn.phone_number || ""}
                            onChange={(e) => {
                              const updated = [...getValues("buttons")];
                              updated[idx].phone_number = e.target.value;
                              setValue("buttons", updated);
                            }}
                            placeholder="+123456789"
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...getValues("buttons")];
                            updated.splice(idx, 1);
                            setValue("buttons", updated);
                          }}
                          className="text-red-500 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    {/* Add Button (limit 3) */}
                    {!getValues("buttons") ||
                    getValues("buttons").length < 3 ? (
                      <button
                        type="button"
                        onClick={() =>
                          setValue("buttons", [
                            ...(getValues("buttons") || []),
                            { type: "QUICK_REPLY", text: "" },
                          ])
                        }
                        className="text-blue-500 hover:underline text-sm"
                      >
                        + Add Button
                      </button>
                    ) : (
                      <p className="text-xs text-red-500">
                        Max 3 buttons allowed
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <DrawerButton
                isSubmitting={loading}
                id={id}
                title={id ? "Update" : "Save"}
              />
            </form>
          </CardBody>
        </Card>
      </Scrollbars>
    </>
  );
};

export default TemplateDrawer;
